import api from "./api";
import type {
  BusinessTrip,
  BusinessTripListResponse,
  BusinessTripCreate,
  BusinessTripUpdate,
  BusinessTripReportCreate,
  BusinessTripReportUpdate,
  BusinessTripFilters,
  ParticipantCreate,
  ProjectInfo,
  ProjectTransferRequest,
  BatchProjectTransferRequest,
  TransferPreviewRequest,
  TransferPreviewResponse,
  TransferHistoryItem,
  BatchTransferResult,
} from "@/types/businessTrip";

export const businessTripService = {
  // === 출장 CRUD ===

  // 출장 목록 조회
  async getBusinessTrips(
    params?: BusinessTripFilters
  ): Promise<BusinessTripListResponse> {
    const response = await api.get<BusinessTripListResponse>("/business-trips", {
      params,
    });
    return response.data;
  },

  // 내 출장 목록 조회
  async getMyBusinessTrips(
    params?: BusinessTripFilters
  ): Promise<BusinessTripListResponse> {
    const response = await api.get<BusinessTripListResponse>(
      "/business-trips/my",
      { params }
    );
    return response.data;
  },

  // 결과보고서 작성 대기 출장 목록
  async getPendingReports(allUsers: boolean = false): Promise<BusinessTrip[]> {
    const response = await api.get<BusinessTrip[]>(
      "/business-trips/pending-reports",
      { params: allUsers ? { all_users: true } : undefined }
    );
    return response.data;
  },

  // 결과보고서 미작성 건수 (사이드바 배지용, 목록과 동일 범위)
  async getPendingReportCount(allUsers: boolean = false): Promise<{ count: number }> {
    const response = await api.get<{ count: number }>(
      "/business-trips/pending-reports/count",
      { params: allUsers ? { all_users: true } : undefined }
    );
    return response.data;
  },

  // 출장 상세 조회
  async getBusinessTrip(tripId: number): Promise<BusinessTrip> {
    const response = await api.get<BusinessTrip>(`/business-trips/${tripId}`);
    return response.data;
  },

  // 출장 등록
  async createBusinessTrip(data: BusinessTripCreate): Promise<BusinessTrip> {
    const response = await api.post<BusinessTrip>("/business-trips", data);
    return response.data;
  },

  // 출장 수정
  async updateBusinessTrip(
    tripId: number,
    data: BusinessTripUpdate
  ): Promise<BusinessTrip> {
    const response = await api.put<BusinessTrip>(
      `/business-trips/${tripId}`,
      data
    );
    return response.data;
  },

  // 출장 삭제
  async deleteBusinessTrip(tripId: number): Promise<void> {
    await api.delete(`/business-trips/${tripId}`);
  },

  // 선택 가능한 프로젝트 목록 조회
  async getAvailableProjects(): Promise<ProjectInfo[]> {
    const response = await api.get<ProjectInfo[]>(
      "/business-trips/available-projects"
    );
    return response.data;
  },

  // === 참여자 관리 ===

  // 출장 참여 신청
  async joinBusinessTrip(tripId: number): Promise<BusinessTrip> {
    const response = await api.post<BusinessTrip>(
      `/business-trips/${tripId}/join`
    );
    return response.data;
  },

  // 출장 참여 취소
  async leaveBusinessTrip(tripId: number): Promise<BusinessTrip> {
    const response = await api.post<BusinessTrip>(
      `/business-trips/${tripId}/leave`
    );
    return response.data;
  },

  // 참여자 추가
  async addParticipant(
    tripId: number,
    data: ParticipantCreate
  ): Promise<BusinessTrip> {
    const response = await api.post<BusinessTrip>(
      `/business-trips/${tripId}/participants`,
      data
    );
    return response.data;
  },

  // 참여자 제거
  async removeParticipant(
    tripId: number,
    userId: string
  ): Promise<BusinessTrip> {
    const response = await api.delete<BusinessTrip>(
      `/business-trips/${tripId}/participants/${userId}`
    );
    return response.data;
  },

  // === 결과보고서 ===

  // 결과보고서 작성
  async createReport(
    tripId: number,
    data: BusinessTripReportCreate
  ): Promise<BusinessTrip> {
    const response = await api.post<BusinessTrip>(
      `/business-trips/${tripId}/report`,
      data
    );
    return response.data;
  },

  // 결과보고서 수정
  async updateReport(
    tripId: number,
    data: BusinessTripReportUpdate
  ): Promise<BusinessTrip> {
    const response = await api.put<BusinessTrip>(
      `/business-trips/${tripId}/report`,
      data
    );
    return response.data;
  },

  // === 프로젝트 이관 ===

  // 출장 프로젝트 이관
  async transferProject(
    tripId: number,
    data: ProjectTransferRequest
  ): Promise<BusinessTrip> {
    const response = await api.put<BusinessTrip>(
      `/business-trips/${tripId}/transfer-project`,
      data
    );
    return response.data;
  },

  // 일괄 프로젝트 이관
  async batchTransferProject(
    data: BatchProjectTransferRequest
  ): Promise<BatchTransferResult> {
    const response = await api.post<BatchTransferResult>(
      "/business-trips/batch-transfer-project",
      data
    );
    return response.data;
  },

  // 이관 미리보기
  async previewTransfer(
    data: TransferPreviewRequest
  ): Promise<TransferPreviewResponse> {
    const response = await api.post<TransferPreviewResponse>(
      "/business-trips/preview-transfer",
      data
    );
    return response.data;
  },

  // 이관 히스토리 조회
  async getTransferHistory(tripId: number): Promise<TransferHistoryItem[]> {
    const response = await api.get<TransferHistoryItem[]>(
      `/business-trips/${tripId}/transfer-history`
    );
    return response.data;
  },
};

export default businessTripService;
